<template>
  <v-menu
    open-on-hover
    open-on-click
    offset-y
    max-width="340"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-chip
        small
        outlined
        color="warning"
        class="mt-1 bill-edit-chip font-weight-bold"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon x-small left>fal fa-pen</v-icon>
        {{ $t("edited_times", { count: editCount }) }}
      </v-chip>
    </template>

    <v-card class="pa-3 text-body-2">
      <div
        v-for="entry in lastEntries"
        :key="entry.kind"
        class="mb-2 bill-edit-entry"
      >
        <div class="d-flex align-center flex-wrap">
          <v-icon x-small left :color="entry.color">{{ entry.icon }}</v-icon>
          <span>
            {{ $t(entry.by) }}:
            <span class="font-weight-bold">{{ entry.user_name || "-" }}</span>
          </span>
          <v-chip v-if="entry.stageLabel" x-small outlined class="ml-2">
            {{ $t(entry.stageLabel) }}
          </v-chip>
          <v-spacer />
          <span class="text-caption text--secondary ml-2">
            {{ entry.created_at | formatDateTime }}
          </span>
        </div>
        <div v-if="entry.items.length">
          {{
            entry.items.map((i) => `${i.name || "-"} × ${i.qty}`).join(" · ")
          }}
        </div>
        <div v-if="entry.reason" class="text-caption text--secondary">
          {{ $t("noted") }}: {{ entry.reason }}
        </div>
      </div>

      <v-divider class="mb-2" />
      <template v-if="cancelSummary || addSummary">
        <!-- The whole movement, so the lines add up whatever was done. -->
        <div class="d-flex justify-space-between amount-row">
          <span>{{ $t("original_total") }}</span>
          <span class="text-no-wrap"
            >{{ originalTotal | NumberFormat }} LAK</span
          >
        </div>
        <div
          v-if="cancelSummary"
          class="d-flex justify-space-between amount-row"
        >
          <span>{{ $t("cancel_total_with_vat") }}</span>
          <span class="text-no-wrap warning--text">
            -{{ cancelSummary.amount | NumberFormat }} LAK
          </span>
        </div>
        <div v-if="addSummary" class="d-flex justify-space-between amount-row">
          <span>{{ $t("add_total_with_vat") }}</span>
          <span class="text-no-wrap info--text">
            +{{ addSummary.amount | NumberFormat }} LAK
          </span>
        </div>
        <div class="d-flex justify-space-between font-weight-bold amount-row">
          <span>{{ $t("current_total") }}</span>
          <span class="text-no-wrap"
            >{{ currentTotal | NumberFormat }} LAK</span
          >
        </div>
      </template>
      <!-- Taken off the open table: never charged, so not part of the sum above -->
      <div
        v-if="preCheckoutSummary"
        class="d-flex justify-space-between amount-row text-caption text--secondary mt-1"
      >
        <span>{{ $t("cancelled_before_checkout_not_charged") }}</span>
        <span class="text-no-wrap">
          {{ preCheckoutSummary.amount | NumberFormat }} LAK
        </span>
      </div>
      <div
        v-if="editCount > lastEntries.length"
        class="text-caption text--secondary mt-2"
      >
        {{ $t("cancel_more_times", { count: editCount - lastEntries.length }) }}
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { myMixin } from "@/mixin/main";

/**
 * One "edited N times" chip for a bill report row, whatever was done to the
 * bill after it was sold (items cancelled, items added). Hover or tap shows
 * the latest of each — who, when, what, why — and how the total moved, so
 * the owner does not have to open the bill. Fed by the row's cancel_summary
 * and add_summary.
 */
export default {
  name: "BillEditBadge",
  mixins: [myMixin],
  props: {
    cancelSummary: { type: Object, default: null },
    addSummary: { type: Object, default: null },
    // Cancels on the open table before checkout: never charged.
    preCheckoutSummary: { type: Object, default: null },
    currentTotal: { type: [Number, String], default: 0 },
  },
  computed: {
    editCount() {
      return (
        (this.cancelSummary ? this.cancelSummary.count : 0) +
        (this.addSummary ? this.addSummary.count : 0) +
        (this.preCheckoutSummary ? this.preCheckoutSummary.count : 0)
      );
    },
    originalTotal() {
      return (
        this.cancelSummary ||
        this.addSummary ||
        this.preCheckoutSummary ||
        {}
      ).original_total;
    },
    lastEntries() {
      const entries = [];
      if (this.cancelSummary) {
        entries.push({
          kind: "cancel",
          color: "warning",
          icon: "fal fa-undo",
          by: "cancelled_by",
          ...this.cancelSummary.last,
        });
      }
      if (this.preCheckoutSummary) {
        entries.push({
          kind: "pre",
          color: "grey",
          icon: "fal fa-utensils",
          by: "cancelled_by",
          stageLabel: "cancelled_before_checkout",
          ...this.preCheckoutSummary.last,
        });
      }
      if (this.addSummary) {
        entries.push({
          kind: "add",
          color: "info",
          icon: "fal fa-plus",
          by: "added_by",
          ...this.addSummary.last,
        });
      }
      return entries.map((entry) => ({ ...entry, items: entry.items || [] }));
    },
  },
  filters: {
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(date.getTime())) return value;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.bill-edit-chip {
  cursor: pointer;
}

/* The orange border and icon carry the meaning; the words use the normal text
   colour, since orange text on white is too faint to read. */
.bill-edit-chip ::v-deep .v-chip__content {
  color: var(--app-text-primary, #333);
}

.amount-row {
  gap: 12px;
}
</style>
